import { createFileRoute, redirect } from '@tanstack/react-router';
import { billingQuery, meQuery } from '../lib/queries.js';

// s120 / s200 — landing-page deep-link entry. The landing's Founder
// Pack and Growth CTAs route here with ?plan=<key> so the dashboard
// knows which tier the visitor was interested in.
//
// Routing rules:
//   logged out                      → stash plan in sessionStorage,
//                                     send to /signup. /onboarding/plan
//                                     reads sessionStorage post-signup.
//   logged in, no active sub        → /onboarding/plan?[prompt=<plan>]
//                                     (treat them as still onboarding —
//                                     the plan picker is the right home
//                                     for "your first checkout").
//   logged in, active subscription  → /settings/billing?prompt=<plan>
//                                     (they're upgrading / switching —
//                                     billing settings is the right home
//                                     for a tier change).
//
// Why sessionStorage and not a URL param through login? Better Auth's
// signin flow doesn't reliably preserve arbitrary query params across
// the credential round-trip; sessionStorage is per-tab, survives the
// auth redirect, and is gone the moment they close the tab. Good match
// for "first-purchase hint that should silently expire."

const VALID_PLANS = new Set(['founder_pack', 'growth']);

export const PENDING_PLAN_STORAGE_KEY = 'magig_pending_plan';

interface StartSearch {
  plan?: 'founder_pack' | 'growth';
}

export const Route = createFileRoute('/start')({
  validateSearch: (search: Record<string, unknown>): StartSearch => {
    if (typeof search['plan'] === 'string' && VALID_PLANS.has(search['plan'])) {
      return { plan: search['plan'] as 'founder_pack' | 'growth' };
    }
    return {};
  },
  beforeLoad: async ({ context: { queryClient }, search }) => {
    const plan = search.plan ?? null;
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) {
      // Logged out: stash the plan so /onboarding/plan (post-signup) +
      // /home (post-login) can pick it up. No-op in non-browser context.
      if (plan && typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem(PENDING_PLAN_STORAGE_KEY, plan);
        } catch {
          // sessionStorage can throw in private-browsing edge cases.
          // Worst case: post-auth lands on a generic page, founder
          // navigates manually. Not a hard failure.
        }
      }
      // [task c59bd78] SIGNUP, not login. This branch is reached by
      // someone who clicked a pricing CTA while logged out — which is
      // to say, overwhelmingly someone who does not have an account.
      // Sending them to a screen that opens "welcome back" and leads
      // with a password field asks them to remember a credential they
      // never made. /signup carries its own link back to /login for
      // the minority who do.
      throw redirect({ to: '/signup' });
    }

    // Logged in. Where to route depends on whether they're "new" (no
    // active subscription) or "existing" (already paying).
    const billing = await queryClient.ensureQueryData(billingQuery());
    const isActiveSubscriber =
      billing.subscriptionStatus === 'active' || billing.subscriptionStatus === 'trialing';

    if (isActiveSubscriber) {
      // Existing customer — route to billing settings for a tier change.
      throw redirect({
        to: '/settings/billing',
        search: plan ? { prompt: plan } : {},
      });
    }

    // No active subscription — they're still onboarding (whether they
    // just signed up or never paid). Land on the plan picker. Stash
    // the deep-linked plan so /onboarding/plan pre-selects it (same
    // sessionStorage mechanism used post-signup, so the picker doesn't
    // need a separate query-param path).
    if (plan && typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem(PENDING_PLAN_STORAGE_KEY, plan);
      } catch {
        // Silent fallback as above.
      }
    }
    throw redirect({ to: '/onboarding/plan' });
  },
  component: () => null,
});
